RivalIQ.apps.Discovery.module('widgets', function (widgets, App, Backbone, Marionette, $, _) {
    'use strict';

    const MAX_NUM_PIE_SLICES = 6;
    const PIE_CHART_SIZE = 130;

    const Panel = RivalIQ.apps.Dashboard.views.Panel.mixin({
        className: 'panel panel-default'
    });

    const PieChartSummaryItem = RivalIQ.apps.Dashboard.views.SummaryItem.mixin({
        template: '#pieTableWidgetSummaryItemTemplate',
        tagName: 'div',

        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        className: function () {
            const me = this;

            let classes = RivalIQ.apps.Dashboard.views.SummaryItem.prototype.className.call(me);
            classes += ' metric-summary-item';

            return classes;
        },

        serializeData: function () {
            const me = this;

            return {
                title: me.model.getName({
                    format: true
                }),
                value: me.model.getValue()
            };
        }
    });


    const PieTableView = RivalIQ.views.LayoutView.mixin({
        template: '#pieTableWidgetTemplate',

        mixins: {
            props: Marionette.Dashboard.mixins.Props,
            normalizeHeights: RivalIQ.apps.Dashboard.mixins.NormalizeHeights,
            LoadDataWithBuffering: RivalIQ.apps.Dashboard.mixins.LoadDataWithBuffering
        },

        dashboardModelEvents: {
            'change': 'reload'
        },

        regions: {
            pieChart: '.x-pie-chart',
            pieChartLegend: '.x-pie-chart-legend',
            table: '.x-table'
        },

        ui: {
            pieTitle: '.x-pie-title',
            table: '.x-table',
            panelBody: '.panel-body'
        },

        _loadData: function () {
            const me = this;

            const table = me.dashboardModel.getWidget('table');
            const analysisProperty = me.getProp('analysisProperty');
            const metricIdx = _.findIndex(table.metricsCollection.models, function (metric) {
                return metric.get('property') === analysisProperty;
            });
            const sortColumns = App.utils.getColumnSortOrder(table.metricsCollection, metricIdx);

            me.dashboardModel.pieChartMetric.orderBy = App.utils.getOrderByString(sortColumns, table.metricsCollection);

            const nameMetric = RivalIQ.metric.build(me.dashboardModel.pieChartMetric, {
                property: 'name'
            });

            const metricsCollection = new RivalIQ.collections.MetricDefinitions([
                nameMetric, me.dashboardModel.pieChartMetric
            ]);

            return metricsCollection.fetch()
                .then(function (results) {
                    me.nameMetric = results.at(0);
                    me.pieChartMetrics = results.at(1);
                });
        },

        onBeforeRenderLoaded: function () {
            const me = this;

            me.showTable();
        },

        onAttachLoaded: function () {
            const me = this;

            if (me.pieChartMetrics.getValue().length) {
                me.ui.panelBody.show();
                me.showChart();
            } else {
                me.ui.panelBody.hide();
            }
        },

        showChart: function () {
            const me = this;

            // Get the value of the first column (hashtags or twitter handles)
            const names = me.nameMetric.getValue();

            const legendMetrics = me.pieChartMetrics.getValue().map(function (pieChartMetric, index) {
                // Need this special case because even though the metric is requesting property `name`,
                // we have a special case in the metric value extraction that maps `name` to `id`.
                let name = names[index][0];
                if (name === '__OTHER__') {
                    name = 'Other';
                }

                return [
                    _.upperFirst(name),
                    index,
                    pieChartMetric[1] // the slice value needs to be last in array
                ];
            });

            const DataCollection = Backbone.Collection.extend({
                model: function (legendMetric) {
                    const metric = me.pieChartMetrics.clone({
                        name: legendMetric[0],
                        colorIndex: legendMetric[1],
                        value: legendMetric[2]
                    });

                    metric.cid = metric.id = legendMetric[0];
                    return metric;
                }
            });

            const metrics = new DataCollection(legendMetrics);

            const legend = me.getLegend(metrics);
            if (legend) {
                me.pieChartLegend.show(legend);
            }

            const chart = new RivalIQ.graph.PieChart({
                height: PIE_CHART_SIZE,
                width: PIE_CHART_SIZE,
                dataProperty: me.dashboardModel.getAnalysisProperty(),
                data: legendMetrics,
                concept: {
                    color: function (datum) {
                        return metrics.get(datum[0]).getColor();
                    },
                    getClassName: _.constant(null)
                }
            });

            me.ui.pieTitle.text(RivalIQ.metric.names.metricName(me.dashboardModel.pieChartMetric));

            me.pieChart.show(chart);
        },

        showTable: function () {
            const me = this;

            const widget = me.dashboardModel.getWidget('table');
            me.table.showWidget(widget, {
                className: `table-double-header ${me.dashboardModel.isExporting() ? 'x-table-container' : 'table-responsive table-responsive-overflow-x-override'}`
            });

            me.listenTo(widget, 'change:sortOrder', function (model, sortOrder) {
                me.setProps('sortOrder', sortOrder, {
                    silent: false
                });
            });

            me.listenTo(widget, 'change:orderBy', function (model, orderBy) {
                me.setProps('orderBy', orderBy, {
                    silent: false
                });
            });
        },

        getLegend: function (metrics) {
            const me = this;

            const legend = new RivalIQ.apps.Dashboard.views.SummaryItems({
                childView: PieChartSummaryItem,
                collection: metrics,
                tagName: 'div',
                childViewOptions: function () {
                    const summaryItemsMe = this;

                    const options = RivalIQ.apps.Dashboard.views.SummaryItems.prototype.childViewOptions.apply(summaryItemsMe, arguments);

                    return _.extend(options, {
                        dashboardModel: me.dashboardModel
                    });
                }
            });

            return legend;
        }
    });

    const BaseWidget = RivalIQ.apps.Dashboard.models.Widget.mixin({
        mixins: {
            exportHD: RivalIQ.apps.Dashboard.mixins.ExportHD
        },

        panelView: Panel,

        exportFormats: [
            RivalIQ.enums.exportFormat.PPT,
            RivalIQ.enums.exportFormat.PDF,
            RivalIQ.enums.exportFormat.PNG,
            RivalIQ.enums.exportFormat.CSV
        ],

        getFilename: App.utils.getExportFilename,
        getExportTitle: App.utils.exportTitle,
        getExportSubtitle: App.utils.exportSubtitle,
        getPptSubtitle: App.utils.pptSubtitle,

        allowPageBreaks: function () {
            const me = this;

            const limit = me.getProp('limit');
            return limit === -1;
        },

        widgetDefinitions: function () {
            const me = this;

            return [{
                instanceId: 'table',
                widgetId: RivalIQ.enums.widgetTypes.DISCOVERY_DIMENSION_ANALYSIS,
                baseMetric: me.baseMetric,
                metrics: me.metrics
            }];
        },

        csvExportProps: function () {
            const me = this;

            return me.getWidget('table').csvExportProps();
        },

        viewMorePath: function () {
            const me = this;
            return me.getPath({
                limit: -1
            });
        }
    });

    const PieTableWidget = BaseWidget.extend({
        view: PieTableView,

        getAnalysisProperty: function () {
            const me = this;
            const channel = me.getProp('channel');
            return (channel === RivalIQ.enums.channels.INSTAGRAM) ? 'num_posts' : 'people_using';
        },

        processProps: function (props) {
            const me = this;

            _.defaults(props, {
                analysisProperty: me.getAnalysisProperty()
            });

            const baseMetric = RivalIQ.metric.build(props, _.clone(me.baseMetric));

            const analysisProperty = props.analysisProperty;
            const pieChartMetricAttrs = _.defaults({
                property: analysisProperty,
                orderBy: _.uniq([analysisProperty, 'avg_engagement_rate_per_post']).join(','),
                sortOrder: 1,
                includeOtherBucket: true,
                limit: MAX_NUM_PIE_SLICES
            }, baseMetric);

            me.pieChartMetric = RivalIQ.metric.build(baseMetric, pieChartMetricAttrs);

            return props;
        }
    });
    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.DISCOVERY_PIE_TABLE, PieTableWidget);

    const TableWithSummaryView = RivalIQ.views.LayoutView.mixin({
        template: '#tableWithSummaryWidgetTemplate',

        mixins: {
            props: Marionette.Dashboard.mixins.Props,
            normalizeHeights: RivalIQ.apps.Dashboard.mixins.NormalizeHeights,
            LoadDataWithBuffering: RivalIQ.apps.Dashboard.mixins.LoadDataWithBuffering
        },

        dashboardModelEvents: {
            'change': 'reload'
        },

        regions: {
            table: '.x-table'
        },

        ui: {
            summaryText: '.table-summary',
            table: '.x-table',
            panelBody: '.panel-body'
        },

        _loadData: function () {
            const me = this;

            const props = me.getProps();

            const baseMetric = RivalIQ.metric.build(props, _.clone(me.dashboardModel.baseMetric));
            const freeDimension = _.head(RivalIQ.metric.getFreeDimensions(baseMetric));
            const summaryMetrics = RivalIQ.metric.build(baseMetric, [
                {
                    dimensions: ['channel'],
                    property: `distinct_${freeDimension}`,
                    orderBy: undefined
                },
                {
                    dimensions: ['channel'],
                    property: `distinct_used_${freeDimension}`,
                    minPeopleUsing: 3,
                    orderBy: undefined
                },
                {
                    dimensions: ['channel', freeDimension],
                    property: 'name',
                    orderBy: 'engagement_total_per_post',
                    sortOrder: 1, // descending
                    limit: 3
                },
                {
                    dimensions: ['channel'],
                    property: 'num_posts'
                },
                {
                    dimensions: ['channel', freeDimension],
                    property: 'num_posts',
                    orderBy: 'num_posts',
                    sortOrder: 1,
                    limit: 3
                }
            ], {
                clobberDimensions: true
            });

            const metricsCollection = new RivalIQ.collections.MetricDefinitions(summaryMetrics);

            const deferred = new $.Deferred();
            metricsCollection.fetch()
                .then(function (results) {
                    me.distinctValuesMetric = results.at(0);
                    me.distinctValueUsersMetric = results.at(1);
                    me.topValuesMetric = results.at(2);
                    me.totalNumPostsMetric = results.at(3);
                    me.topDimensionalNumPostsMetric = results.at(4);

                    const dimensionalNumPosts = _.reduce(me.topDimensionalNumPostsMetric.getValue({
                        format: false
                    }), function (memo, entity) {
                        const hashtag = entity[0];
                        if (hashtag !== props.searchTerm) {
                            memo.push(entity);
                        }
                        return memo;
                    }, []);

                    if (dimensionalNumPosts.length) {
                        const secondLargestNumPosts = dimensionalNumPosts.length > 1 ? dimensionalNumPosts[1][1] : dimensionalNumPosts[0][1];

                        const defaultNotableUsageAmount = App.enums.getDefaultNotableUsageAmount(me.totalNumPostsMetric.getValue({
                            format: false
                        }));
                        const altNotableUsageAmount = App.enums.getRelaxedNotableUsageAmount(secondLargestNumPosts);
                        const commonProperties = {
                            dimensions: ['channel'],
                            property: `often_used_${freeDimension}`,
                            orderBy: undefined
                        };
                        const numUsedMetrics = RivalIQ.metric.build(baseMetric, [_.extend(_.clone(commonProperties), {
                            notableUsageAmount: defaultNotableUsageAmount
                        }), _.extend(_.clone(commonProperties), {
                            notableUsageAmount: altNotableUsageAmount
                        })], {
                            clobberDimensions: true
                        });

                        const secondaryMetricsCollection = new RivalIQ.collections.MetricDefinitions(numUsedMetrics);

                        secondaryMetricsCollection.fetch()
                            .then(function (secondaryResults) {
                                me.defaultNumOftenUsedMetric = secondaryResults.at(0);
                                me.relaxedNumOftenUsedMetric = secondaryResults.at(1);
                            })
                            .done(deferred.resolve);
                    } else {
                        deferred.resolve();
                    }
                });
            return deferred;
        },

        onBeforeRenderLoaded: function () {
            const me = this;

            me.showTable();
        },

        onAttachLoaded: function () {
            const me = this;

            if (me.distinctValuesMetric.getValue({
                format: false
            }) > 0) {
                me.ui.panelBody.show();
                me.showSummaryText();
            } else {
                me.ui.panelBody.hide();
            }
        },

        showSummaryText: function () {
            const me = this;

            const baseMetric = RivalIQ.metric.build(me.getProps(), _.clone(me.dashboardModel.baseMetric));
            const freeDimensionName = _.head(RivalIQ.metric.getFreeDimensions(baseMetric));
            const numDistinctValues = me.distinctValuesMetric.getValue({
                format: false
            });
            let valueCountText = 'There ';
            switch (numDistinctValues) {
                case 0:
                    valueCountText += ` are no ${freeDimensionName}s`;
                    break;

                case 1:
                    valueCountText += ` is ${$.tagHtml('strong', {
                        text: `1 ${freeDimensionName}`
                    })}`;
                    break;

                default:
                    valueCountText += ` are ${$.tagHtml('strong', {
                        text: `${me.distinctValuesMetric.getValue()} ${freeDimensionName}s`
                    })}`;
            }
            valueCountText += ' in this dataset.';

            let numUsedText = '';
            if ((me.getProp('channel') === RivalIQ.enums.channels.INSTAGRAM) && Boolean(me.defaultNumOftenUsedMetric)) {
                let numOftenUsed = me.defaultNumOftenUsedMetric.getValue({
                    format: false
                });
                let notableUsageAmount = me.defaultNumOftenUsedMetric.get('notableUsageAmount');
                if (numOftenUsed === 0) {
                    numOftenUsed = me.relaxedNumOftenUsedMetric.getValue({
                        format: false
                    });
                    notableUsageAmount = me.relaxedNumOftenUsedMetric.get('notableUsageAmount');
                }
                numUsedText = `Of these, ${numOftenUsed} coincident ${freeDimensionName}${(numOftenUsed === 1) ? ' was' : 's were'} used ${notableUsageAmount} or more times.`;
            } else {
                switch (me.distinctValueUsersMetric.getValue({
                    format: false
                })) {
                    case 0:
                        numUsedText = `There were no ${freeDimensionName}s used by more than 3 people.`;
                        break;

                    case 1:
                        numUsedText = `There was only one ${freeDimensionName} used by more than 3 people.`;
                        break;

                    default:
                        numUsedText = `Of these, ${me.distinctValueUsersMetric.getValue()} ${freeDimensionName}s were used by 3 or more people.`;
                }
            }

            let topValuesText = '';
            const topValues = me.topValuesMetric.getValue();
            if (topValues.length > 0) {
                topValuesText = `The ${freeDimensionName}s with the highest engagement per ${
                    RivalIQ.data.channels.get(me.getProp('channel')).get('postName')
                } include `;
                topValuesText += `${_.reduce(topValues, function (memo, value, idx) {
                    if (idx === 0) {
                        memo = '';
                    } else if (idx === topValues.length - 1) {
                        memo += ', and ';
                    } else {
                        memo += ', ';
                    }

                    return memo + $.tagHtml('strong', {
                        text: _.last(value)
                    });
                }, '')}.`;
            }

            const breadthSummary = [
                $.tagHtml('p', {
                    html: [valueCountText, numUsedText].join(' ')
                }),
                $.tagHtml('p', {
                    html: topValuesText
                })
            ];

            me.ui.summaryText.html(breadthSummary.join(' '));
        },

        showTable: function () {
            const me = this;

            const widget = me.dashboardModel.getWidget('table');
            me.table.showWidget(widget, {
                className: `table-double-header ${me.dashboardModel.isExporting() ? 'x-table-container' : 'table-responsive table-responsive-overflow-x-override'}`
            });

            me.listenTo(widget, 'change:sortOrder', function (model, sortOrder) {
                me.setProps('sortOrder', sortOrder, {
                    silent: false
                });
            });

            me.listenTo(widget, 'change:orderBy', function (model, orderBy) {
                me.setProps('orderBy', orderBy, {
                    silent: false
                });
            });
        }
    });
    const TableWithSummaryWidget = BaseWidget.extend({
        view: TableWithSummaryView
    });
    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.DISCOVERY_SUMMARY_TABLE, TableWithSummaryWidget);
});
